import {
  AdminLevelType,
  DecisionType,
  ExaminationStatus,
  ExaminationType,
  ExecutionType,
  OrganizationAdminLevelPermission,
  OrganizationType,
  OrganizationUserRole,
  ProcessAllocationCostsType,
  ProcessAttachmentType,
  ProcessImplementationMethod,
  ProcessImplementationType,
  ProcessReason,
  ProcessStatus,
  SignCategory,
  SignCondition,
  SignGroupInstallationSide,
  SignGroupType,
  SignPermanency,
  SignReflectionClass,
  SignSize,
  SignSourceType,
  SignStatusType,
  SignSupportType,
  UserInstitutionType,
  UserNotificationInterval,
  UserRole,
  UserStatus,
} from '@/modules/api';
import { EntityActive } from '@/types/EntityActive';
import { MapLayer } from '@/types/MapLayer';
import { MapStyle } from '@/types/MapStyle';
import { ProcessType } from '@/types/ProcessType';

export const I18N_DE = {
  AdminLevelType: {
    [AdminLevelType.STATE]: 'Bundesland',
    [AdminLevelType.GOVERNMENT_DISTRICT]: 'Regierungsbezirk',
    [AdminLevelType.COUNTY]: 'Landkreis',
    [AdminLevelType.CITY_COUNTY]: 'Stadtkreis',
    [AdminLevelType.MUNICIPALITY]: 'Gemeinde',
  },
  AdminLevelTypeAbbreviation: {
    [AdminLevelType.STATE]: 'Bl.',
    [AdminLevelType.GOVERNMENT_DISTRICT]: 'Reg.-Bez.',
    [AdminLevelType.COUNTY]: 'Lk.',
    [AdminLevelType.CITY_COUNTY]: 'Stdk.',
    [AdminLevelType.MUNICIPALITY]: 'Gde.',
  },

  EntityActive: {
    [EntityActive.TRUE]: 'Aktiv',
    [EntityActive.FALSE]: 'Inaktiv',
  },
  EntityActiveDescription: {
    [EntityActive.TRUE]: 'Die Entität ist aktiv.',
    [EntityActive.FALSE]: 'Die Entität ist inaktiv.',
  },

  ProcessType: {
    [ProcessType.NORMAL]: 'Neuer Vorgang',
    [ProcessType.EXTERNAL]: 'Externe Anordnung',
  },
  ProcessTypeDescription: {
    [ProcessType.NORMAL]: 'Digitale Anordnung erstellen',
    [ProcessType.EXTERNAL]: 'Bestehendes Anordnungsdokument verknüpfen',
  },

  MapStyle: {
    [MapStyle.VECTOR]: 'basemap.de',
    [MapStyle.SATELLITE]: 'Luftbilder',
  },

  OrganizationAdminLevelPermission: {
    [OrganizationAdminLevelPermission.EDIT]: 'Bearbeiten',
    [OrganizationAdminLevelPermission.VIEW]: 'Ansehen',
  },
  OrganizationAdminLevelPermissionDescription: {
    [OrganizationAdminLevelPermission.EDIT]:
      'Ermöglicht die Bearbeitung der Schilder der Verwaltungsebene sowie untergeordneten Verwaltungsebenen.',
    [OrganizationAdminLevelPermission.VIEW]:
      'Ermöglicht die Betrachtung der Schilder der Verwaltungsebene sowie untergeordneten Verwaltungsebenen.',
  },
  OrganizationType: {
    [OrganizationType.POLICE]: 'Polizei',
    [OrganizationType.FEDERAL_AUTHORITY]: 'Bundesbehörde',
    [OrganizationType.ROAD_TRAFFIC_AUTHORITY]: 'Straßenverkehrsbehörde',
    [OrganizationType.ROAD_CONSTRUCTION_AUTHORITY]: 'Straßenbaubehörde',
    [OrganizationType.ROAD_MAINTENANCE_AUTHORITY]: 'Straßenmeisterei',
    [OrganizationType.REGIONAL_COUNCIL]: 'Regierungspräsidium',
    [OrganizationType.DISTRICT_OFFICE]: 'Landratsamt',
    [OrganizationType.URBAN_DISTRICT]: 'Stadtkreis',
    [OrganizationType.DISTRICT_CAPITAL]: 'Kreisstadt',
    [OrganizationType.MUNICIPALITY]: 'Gemeinde',
    [OrganizationType.MISCELLANEOUS]: 'Sonstige',
  },
  OrganizationUserRole: {
    [OrganizationUserRole.MANAGER]: 'Manager*in',
    [OrganizationUserRole.EDITOR]: 'Bearbeiter*in',
  },
  OrganizationUserRoleDescription: {
    [OrganizationUserRole.MANAGER]:
      'Ermöglicht die Bearbeitung der Organisation sowie deren Schilder und Benutzer*innen.',
    [OrganizationUserRole.EDITOR]: 'Ermöglicht die Bearbeitung der Schilder der Organisation.',
  },

  SignCategory: {
    [SignCategory.ADDITION]: 'Zusatzzeichen',
    [SignCategory.DANGER]: 'Gefahrzeichen',
    [SignCategory.DIRECTION]: 'Richtzeichen',
    [SignCategory.MARKING]: 'Markierung',
    [SignCategory.OTHER]: 'Sonstiges Zeichen',
    [SignCategory.REGULATION]: 'Vorschriftzeichen',
    [SignCategory.TRAFFIC_FACILITY]: 'Verkehrseinrichtung',
    [SignCategory.TRAFFIC_GUIDANCE]: 'Verkehrslenkungstafel',
  },
  SignCategoryPlural: {
    [SignCategory.ADDITION]: 'Zusatzzeichen',
    [SignCategory.DANGER]: 'Gefahrzeichen',
    [SignCategory.DIRECTION]: 'Richtzeichen',
    [SignCategory.MARKING]: 'Markierungen',
    [SignCategory.OTHER]: 'Sonstige Zeichen',
    [SignCategory.REGULATION]: 'Vorschriftzeichen',
    [SignCategory.TRAFFIC_FACILITY]: 'Verkehrseinrichtungen',
    [SignCategory.TRAFFIC_GUIDANCE]: 'Verkehrslenkungstafeln',
  },
  SignStatusType: {
    [SignStatusType.DRAFT]: 'Entwurf',
    [SignStatusType.PLACED]: 'Aufgestellt',
    [SignStatusType.DELETED]: 'Entfernt',
    MODIFIED: 'Geändert',
  },
  SignSupportType: {
    [SignSupportType.POST]: 'Pfosten',
    [SignSupportType.FORK_STAND]: 'Gabelständer',
    [SignSupportType.TRI_POST]: 'Trimast',
    [SignSupportType.WOOD_POST]: 'Holzpfosten',
    [SignSupportType.OVERHEAD]: 'Überkopfmasten',
    [SignSupportType.OTHER]: 'Sonstige',
    [SignSupportType.UNKNOWN]: 'Unbekannt',
  },
  SignSourceType: {
    [SignSourceType.MANUAL]: 'Manuell erstellt',
    [SignSourceType.EXTERNAL]: 'Externe Datenquelle',
    [SignSourceType.MOBILE_MAPPING]: 'Befahrung',
  },
  SignSize: {
    [SignSize.SIZE_1]: '1 (0-20 km/h)',
    [SignSize.SIZE_2]: '2 (21-80 km/h)',
    [SignSize.SIZE_3]: '3 (ab 80 km/h)',
  },
  SignSizeShort: {
    [SignSize.SIZE_1]: '1',
    [SignSize.SIZE_2]: '2',
    [SignSize.SIZE_3]: '3',
  },
  SignReflectionClass: {
    [SignReflectionClass.RA_1]: 'RA 1',
    [SignReflectionClass.RA_2]: 'RA 2',
    [SignReflectionClass.RA_3]: 'RA 3',
  },
  SignGroupInstallationSide: {
    [SignGroupInstallationSide.LEFT]: 'Links',
    [SignGroupInstallationSide.RIGHT]: 'Rechts',
  },
  SignGroupType: {
    [SignGroupType.FRAME]: 'Rahmen',
    [SignGroupType.NONE]: 'Logische Verbindung',
  },

  UserRole: {
    [UserRole.ADMIN]: 'Admin',
    [UserRole.USER]: 'Benutzer*in',
  },
  UserRoleDescription: {
    [UserRole.ADMIN]:
      'Ermöglicht den Zugriff auf alle Meldungen, Verwaltungsebenen und Benutzer*innen auf der Plattform.',
    [UserRole.USER]:
      'Ermöglicht ausschließlich den Zugriff auf zugeordnete Verwaltungsebenen und deren zugehörigen Meldungen und Benutzer*innen.',
  },
  UserStatus: {
    [UserStatus.ACTIVE]: 'Aktiv',
    [UserStatus.INACTIVE]: 'Inaktiv',
    [UserStatus.REGISTERED]: 'Registriert',
  },
  UserStatusDescription: {
    [UserStatus.ACTIVE]: 'Es besteht Zugriff auf die Plattform.',
    [UserStatus.INACTIVE]: 'Der Zugriff auf die Plattform ist gesperrt.',
    [UserStatus.REGISTERED]: 'Der/die Benutzer*in wartet auf Aktivierung.',
  },
  UserNotificationInterval: {
    [UserNotificationInterval.IMMEDIATE]: 'Unmittelbar',
    [UserNotificationInterval.DAILY]: 'Täglich',
    [UserNotificationInterval.WEEKDAYS]: 'Werktags (Mo.-Fr.)',
    [UserNotificationInterval.WEEKLY]: 'Wöchentlich',
    [UserNotificationInterval.NEVER]: 'Keine',
  },
  UserInstitutionType: {
    [UserInstitutionType.AUTHORITY]: 'Behörde',
    [UserInstitutionType.COMPANY]: 'Unternehmen',
    [UserInstitutionType.EDUCATIONAL_INSTITUTION]: 'Bildungseinrichtung',
    [UserInstitutionType.CLUB]: 'Verein',
    [UserInstitutionType.OTHER]: 'Sonstige',
  },
  MapLayer: {
    [MapLayer.ATKIS_PATHS]: 'ATKIS Wegeachsen',
    [MapLayer.ATKIS_STREETS]: 'ATKIS Straßenachsen',
  },
  ProcessStatus: {
    [ProcessStatus.DRAFT]: 'Entwurf',
    [ProcessStatus.EXAMINATION]: 'Prüfung',
    [ProcessStatus.DECISION]: 'Entscheidung',
    [ProcessStatus.EXECUTION]: 'Ausführung',
    [ProcessStatus.FINALIZATION]: 'Abschluss',
    [ProcessStatus.ARCHIVED]: 'Archiviert',
    [ProcessStatus.CLOSED]: 'Geschlossen',
    [ProcessStatus.REJECTED]: 'Abgelehnt',
    [ProcessStatus.CANCELLED]: 'Abgebrochen',
  },
  SignPermanency: {
    [SignPermanency.DYNAMIC]: 'dynamisch',
    [SignPermanency.STATIC]: 'statisch',
    [SignPermanency.VARIABLE]: 'digital',
  },
  SignCondition: {
    [SignCondition.OK]: 'In Ordnung',
    [SignCondition.FADED]: 'Verblasst',
    [SignCondition.STICKERED]: 'Beklebt',
    [SignCondition.COVERED]: 'Verdeckt',
    [SignCondition.DIRTY]: 'Beschmutzt',
    [SignCondition.DAMAGED]: 'Beschädigt',
  },
  CreateExaminationType: {
    [ExaminationType.ASSIGNED]: 'Bearbeiter*in zuweisen',
    [ExaminationType.LINK]: 'Link zur Eingabe',
    [ExaminationType.SELF_INPUT]: 'Eingabe der Absprache',
  },
  ExaminationType: {
    [ExaminationType.ASSIGNED]: 'Bearbeiter*in zugewiesen',
    [ExaminationType.LINK]: 'Link zur Eingabe',
    [ExaminationType.SELF_INPUT]: 'Eingabe der Absprache',
  },
  ExaminationStatus: {
    [ExaminationStatus.OPEN]: 'offen',
    [ExaminationStatus.APPROVED]: 'genehmigt',
    [ExaminationStatus.REJECTED]: 'abgelehnt',
    [ExaminationStatus.CANCELLED]: 'abgebrochen',
  },
  DecisionType: {
    [DecisionType.AUTHORIZE]: 'VRAO veranlassen',
    [DecisionType.ABORT]: 'VRAO abbrechen',
  },
  ExecutionType: {
    [ExecutionType.INSPECTION]: 'Vor Ort Überprüfung',
    [ExecutionType.ARRANGEMENT]: 'Aufstellung / Auftragung',
    [ExecutionType.REMOVAL]: 'Entfernen / Abtragung',
  },
  ProcessReason: {
    [ProcessReason.SAFETY_AND_REGULATION]: 'aus Gründen der Sicherheit und Ordnung',
    [ProcessReason.PRESERVATION_NIGHTS_REST]: 'zum Schutze der Nachtruhe',
    [ProcessReason.PROTECTED_LANDSCAPE]: 'zum Schutz vor Belästigungen in Landschaftsschutzgebieten',
    [ProcessReason.ROAD_DAMAGE]: 'zur Verhütung außerordentlicher Schäden an der Straße',
    [ProcessReason.URBAN_DEVELOPMENT]: 'aus städtebaulichen Gründen',
  },
  ProcessImplementationMethod: {
    [ProcessImplementationMethod.INSTALLATION_OR_APPLICATION]: 'Aufstellung / Auftragung',
    [ProcessImplementationMethod.ADAPTION_OR_MODIFICATION]: 'Anpassung / Änderung',
    [ProcessImplementationMethod.REMOVAL]: 'Entfernung',
  },
  ProcessImplementationType: {
    [ProcessImplementationType.ROAD_MARKING]: 'Fahrbahnmarkierung',
    [ProcessImplementationType.TRAFFIC_SIGN]: 'Verkehrszeichen',
    [ProcessImplementationType.TRAFFIC_EQUIPMENT]: 'Verkehrseinrichtung',
    [ProcessImplementationType.TRAFFIC_LIGHT]: 'Lichtzeichenanlage',
    [ProcessImplementationType.STOPPING_RESTRICTION_PROCESSIONS]: 'Halteverbot für Umzüge',
    [ProcessImplementationType.STOPPING_RESTRICTION_FILM_EVENTS]: 'Halteverbot für Filmveranstaltungen',
    [ProcessImplementationType.STOPPING_RESTRICTION_STREET_CLEANING]: 'Halteverbot für Straßenreinigung',
    [ProcessImplementationType.STOPPING_RESTRICTION_CITIZENS_OFFICE]: 'Halteverbot für Bürgeramt',
  },
  ProcessAllocationCostsType: {
    [ProcessAllocationCostsType.STVG_PARAGRAPH_5B_1]: '§ 5b Abs. 1 StVG',
    [ProcessAllocationCostsType.STVG_PARAGRAPH_5B_2]: '§ 5b Abs. 2 StVG',
    [ProcessAllocationCostsType.STVG_PARAGRAPH_5B_6]: '§ 5b Abs. 6 StVG',
  },
  ProcessAttachmentType: {
    [ProcessAttachmentType.LISTED_TRAFFIC_RESTRICTIONS]:
      'Die aufgeführten Verkehrsbeschränkungen sind Bestandteil dieser Anordnung.',
    [ProcessAttachmentType.MEMO]: 'Die Aktennotiz ist Bestandteil dieser Anordnung.',
    [ProcessAttachmentType.ATTACHMENTS]:
      'Beigefügte Anlage(n) ist/sind Bestandteil dieser Anordnung. Darunter genaue Auflistung der angehängten Dokumente: z.B. Lageplan',
  },
  ErrorCode: {
    400: 'Ungültige Anfrage',
    401: 'Nicht authorisiert',
    403: 'Nicht erlaubt',
    404: 'Nicht gefunden',
    500: 'Interner Fehler',
    503: 'Dienst nicht verfügbar',
  },
};
